import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { useAuthContext } from "@/context/authentication/auth-context"
import { toast } from "@/hooks/use-toast"
import {
  useRegisterAddress,
  useRegisterBankDetails,
  useUpdateHealth,
  useUpdateProfile,
} from "@/services/queries/usePerson"
import type { FieldConfig } from "@/components/form-builder"
import { MARITAL_STATUS_OPTIONS, PRONOUNS_OPTIONS } from "../constants/marital-status-options"
import { personPFSchema } from "../schemas/person-pf-schema"
import type { PersonPFFormData } from "../types/person-pf"
import {
  transformPFFormToAddress,
  transformPFFormToBank,
  transformPFFormToHealth,
  transformPFFormToProfile,
} from "../utils/person-transformers"
import { useCepAutofill } from "./use-cep-autofill"

const ALLOW_PARTIAL_SAVE = true

interface UsePersonPFAddProps {
  onSuccess: () => void
}

export function usePersonPFAdd({ onSuccess }: UsePersonPFAddProps) {
  const { person } = useAuthContext()

  function formatDate(dateString: string) {
    const datePart = dateString.split('T')[0];
    const [year, month, day] = datePart.split('-');
    return `${day}/${month}/${year}`;
  }

  const form = useForm<PersonPFFormData>({
    resolver: zodResolver(personPFSchema),
    defaultValues: {
      completeName: person?.name || "",
      socialName: person?.social_name || "",
      artisticName: person?.artistic_name || "",
      role: person?.role || "",
      rg: person?.rg_number || "",
      cpf: person?.document || "",
      birthDate: person?.birth_date ? formatDate(person.birth_date) : "",
      birthPlace: person?.place_of_birth || "",
      nationality: person?.nationality || "",
      countryEntryDate: person?.brazil_arrival_date ? formatDate(person.brazil_arrival_date) : "",
      maritalStatus: person?.id_civil_state?.toString() || "",
      pronouns: person?.id_pronouns?.toString() || "",
      cellphone: person?.cellphone_number || "",
      tshirtNumber: person?.tshirt_number || "",
      residentialAddress: person?.addresses?.[0]?.address || "",
      neighborhood: person?.addresses?.[0]?.neighbourhood || "",
      city: "",
      state: "",
      zipCode: person?.addresses?.[0]?.postal_code || "",
    },
  })

  useCepAutofill(form, "zipCode")

  const updateProfile = useUpdateProfile(false)
  const registerAddress = useRegisterAddress(false)
  const updateHealth = useUpdateHealth(false)
  const registerBank = useRegisterBankDetails(false)

  function validateForm(data: PersonPFFormData) {
    if (data.completeName.trim() === "" && 
      data.rg.trim() === "" &&
      data.role.trim() === "" && 
      data.birthDate === undefined &&
      data.birthPlace.trim() === "" &&
      data.maritalStatus.trim() === "" &&
      data.pronouns.trim() === "" &&
      data.cellphone.trim() === "" &&
      data.zipCode.trim() === "" &&
      data.residentialAddress.trim() === "" &&
      data.neighborhood.trim() === "" &&
      data.city.trim() === ""
    ) {
      form.handleSubmit(() => {})

      toast({
        title: "Formulário inválido",
        description: "Preencha todos os campos obrigatórios.",
        variant: "destructive",
      })
      return false
    }
    return true
  }

  const onFormSubmit = async (data: PersonPFFormData) => {
    if (!validateForm(data)) {
      return
    }

    const rollbackQueue: (() => Promise<void>)[] = []
    try {
      const profileResult = await updateProfile.mutateAsync(
        transformPFFormToProfile(data)
      )
      rollbackQueue.push(async () => {
        void profileResult
      })

      const addressResult = await registerAddress.mutateAsync(
        transformPFFormToAddress(data)
      )
      rollbackQueue.push(async () => {
        void addressResult
      })

      const healthResult = await updateHealth.mutateAsync(
        transformPFFormToHealth(data)
      )
      rollbackQueue.push(async () => {
        void healthResult
      })

      const bankResult = await registerBank.mutateAsync(
        transformPFFormToBank(data)
      )
      rollbackQueue.push(async () => {
        void bankResult
      })

      toast({
        title: "Dados salvos com sucesso",
        variant: "success",
      })
      onSuccess()
    } catch (error) {
      if (ALLOW_PARTIAL_SAVE) {
        await Promise.all(
          rollbackQueue.reverse().map((fn) => fn().catch((err) => console.error(err)))
        )
        toast({
          title: "Erro ao salvar dados",
          description: `Nenhum dado foi salvo. Tente novamente.${error}`,
          variant: "destructive",
        })
        return
      }

      toast({
        title: "Erro parcial",
        description: "Alguns dados podem ter sido salvos. Verifique sua conta.",
        variant: "destructive",
      })
    }
  }

  const personalInfoFields: FieldConfig[] = [
    { name: "completeName", type: "text", label: "Nome Completo", required: true },
    { name: "socialName", type: "text", label: "Nome Social" },
    { name: "artisticName", type: "text", label: "Nome Artístico" },
    { name: "role", type: "text", label: "Função", required: true },
    { name: "rg", type: "rg", label: "Nº RG", placeholder: "Digite o número do seu RG", required: true },
    { name: "cpf", type: "cpf", label: "Nº CPF", required: true },
    { name: "birthDate", type: "date-inline", label: "Data de Nascimento", required: true },
    { name: "birthPlace", type: "text", label: "Local de Nascimento (cidade e estado)", required: true },
    { name: "nationality", type: "text", label: "Nacionalidade", required: true },
    { name: "countryEntryDate", type: "date-inline", label: "Data de Entrada no País (caso se aplique)" },
    {
      name: "maritalStatus",
      type: "select",
      label: "Estado Civil",
      required: true,
      options: MARITAL_STATUS_OPTIONS,
    },
    {
      name: "pronouns",
      type: "select",
      label: "Pronomes",
      required: true,
      options: PRONOUNS_OPTIONS,
    },
    { name: "cellphone", type: "phone", label: "Celular", required: true },
    { name: "tshirtNumber", type: "text", label: "Número da Camiseta" },
  ]

  const addressFields: FieldConfig[] = [
    { name: "zipCode", type: "cep", label: "CEP", required: true },
    {
      name: "address",
      type: "text",
      label: "Endereço residencial (PF)",
      required: true,
      readOnly: true,
    },
    {
      name: "neighborhood",
      type: "text",
      label: "Bairro (PF)",
      required: true,
      readOnly: true,
    },
    {
      name: "city",
      type: "text",
      label: "Cidade, Estado (PF)",
      required: true,
      readOnly: true,
    },
  ]

  const healthFields: FieldConfig[] = [
    { name: "healthPlan", type: "text", label: "Plano de Saúde" },
    { name: "planCoverage", type: "text", label: "Cobertura do Plano" },
    { name: "emergencyContact", type: "text", label: "Contato de Emergência" },
    { name: "bloodType", type: "text", label: "Tipo Sanguíneo" },
    { name: "allergies", type: "text", label: "Alergias" },
    {
      name: "coronavirusVaccination",
      type: "text",
      label: "Vacinação Covid-19",
    },
    { name: "dietaryRestrictions", type: "text", label: "Restrições Alimentares" },
  ]

  const bankFields: FieldConfig[] = [
    { name: "bank", type: "text", label: "Banco" },
    { name: "branch", type: "text", label: "Agência" },
    { name: "checkingAccount", type: "text", label: "Conta Corrente" },
    { name: "pixKey", type: "text", label: "Chave Pix" },
  ]

  const isPending =
    updateProfile.isPending ||
    registerAddress.isPending ||
    updateHealth.isPending ||
    registerBank.isPending

  return {
    form,
    isPending,
    onFormSubmit,
    personalInfoFields,
    addressFields,
    healthFields,
    bankFields,
  }
}
